import React, { useState } from 'react';
import { MetricsCard } from '@/components/MetricsCard';
import { formatPercentage, cn } from '@/utils';

interface AnalyticsProps {
  selectedChannel: string;
}

export const Analytics: React.FC<AnalyticsProps> = ({ selectedChannel: _selectedChannel }) => {
  const [timeframe, setTimeframe] = useState<'7d' | '30d' | '90d'>('7d');

  // Channel Performance Data
  const channelPerformanceData = [
    {
      channel: 'Email',
      delivery: 72.4,
      engagement: 24.6,
      response: 0.7,
    },
    {
      channel: 'SMS',
      delivery: 94.2,
      engagement: 8.2,
      response: 12.4,
    },
    {
      channel: 'Voice',
      delivery: 43.7,
      engagement: 67.0,
      response: 23.0,
    },
    {
      channel: 'Chat',
      delivery: 89.1,
      engagement: 78.0,
      response: 45.0,
    },
  ];

  // Heat map data
  const heatMapData = [
    { day: 'Mon', '6AM': 2, '9AM': 4, '12PM': 3, '3PM': 4, '6PM': 3, '9PM': 2, '12AM': 0 },
    { day: 'Tue', '6AM': 2, '9AM': 4, '12PM': 4, '3PM': 4, '6PM': 3, '9PM': 2, '12AM': 0 },
    { day: 'Wed', '6AM': 2, '9AM': 4, '12PM': 3, '3PM': 4, '6PM': 4, '9PM': 2, '12AM': 0 },
    { day: 'Thu', '6AM': 2, '9AM': 4, '12PM': 4, '3PM': 4, '6PM': 4, '9PM': 3, '12AM': 0 },
    { day: 'Fri', '6AM': 2, '9AM': 3, '12PM': 3, '3PM': 3, '6PM': 2, '9PM': 0, '12AM': 0 },
    { day: 'Sat', '6AM': 0, '9AM': 2, '12PM': 3, '3PM': 3, '6PM': 2, '9PM': 0, '12AM': 0 },
    { day: 'Sun', '6AM': 0, '9AM': 2, '12PM': 2, '3PM': 2, '6PM': 0, '9PM': 0, '12AM': 0 },
  ];

  // Cohort data
  const cohortData = [
    { cohort: '2026-04-01', size: 847, week1: 72.4, week2: 45.2, week4: 28.7, week8: 19.1, final: 18.3 },
    { cohort: '2026-03-25', size: 923, week1: 74.1, week2: 46.8, week4: 29.4, week8: 20.2, final: 19.7 },
    { cohort: '2026-03-18', size: 756, week1: 71.8, week2: 44.1, week4: 27.9, week8: 18.8, final: 17.9 },
    { cohort: '2026-03-11', size: 834, week1: 73.2, week2: 47.3, week4: 30.1, week8: 21.4, final: 20.8 },
    { cohort: '2026-03-04', size: 789, week1: 70.9, week2: 43.7, week4: 26.8, week8: 17.9, final: 17.1 },
    { cohort: '2026-02-25', size: 892, week1: 75.3, week2: 48.9, week4: 31.2, week8: 22.1, final: 21.5 },
  ];

  const getHeatmapColor = (value: number) => {
    if (value >= 4) return 'bg-blue-500';
    if (value >= 3) return 'bg-blue-400';
    if (value >= 2) return 'bg-blue-300';
    if (value >= 1) return 'bg-amber-400';
    return 'bg-slate-600';
  };

  return (
    <div className="space-y-6 p-6">
      {/* Performance Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricsCard
          title="Engagement Rate"
          value={formatPercentage(24.6)}
          change={{ value: 2.1, label: 'WoW' }}
        />
        <MetricsCard
          title="Conversion Rate"
          value={formatPercentage(18.3)}
          change={{ value: 1.4, label: 'WoW' }}
        />
        <MetricsCard
          title="Handoff Quality"
          value="8.7/10"
          change={{ value: -0.2, label: 'WoW' }}
        />
        <MetricsCard
          title="Avg Response Time"
          value="47 minutes"
          change={{ value: -8, label: 'min WoW' }}
        />
        <MetricsCard
          title="Sequence Completion"
          value={formatPercentage(67.3)}
          change={{ value: -2.1, label: 'WoW' }}
        />
        <MetricsCard
          title="Cost per Lead"
          value="$24.50"
          change={{ value: -3.20, label: 'WoW' }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Channel Performance */}
        <div className="bg-slate-700 rounded-lg border border-slate-600 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-50">
              Channel Performance Analysis
            </h2>
            <div className="flex space-x-2">
              {(['7d', '30d', '90d'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setTimeframe(period)}
                  className={cn(
                    'px-3 py-1 text-sm rounded',
                    timeframe === period 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                  )}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {channelPerformanceData.map((channel) => (
              <div key={channel.channel} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-slate-300">
                    📧 {channel.channel}
                  </span>
                </div>
                
                <div className="bg-slate-800 rounded p-3">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-400">Delivery</span>
                    <span className="text-slate-300">{formatPercentage(channel.delivery)}</span>
                  </div>
                  <div className="bg-slate-600 rounded-full h-2 mb-2">
                    <div 
                      className="bg-emerald-500 h-2 rounded-full" 
                      style={{ width: `${channel.delivery}%` }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs text-slate-400">
                    <div>Engagement: {formatPercentage(channel.engagement)}</div>
                    <div>Response: {formatPercentage(channel.response)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-slate-800 rounded">
            <div className="text-sm text-emerald-400 mb-1">Best Performers:</div>
            <div className="text-xs text-slate-400">SMS (response) • Chat (engagement)</div>
            <div className="text-sm text-amber-400 mb-1 mt-2">Needs Attention:</div>
            <div className="text-xs text-slate-400">Voice (pickup rate) • Email (deliverability)</div>
          </div>
        </div>

        {/* Engagement Heat Map */}
        <div className="bg-slate-700 rounded-lg border border-slate-600 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-50">
              Engagement Heat Map
            </h2>
            <div className="flex space-x-2">
              <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded">Hour</button>
              <button className="px-3 py-1 text-sm bg-slate-600 text-slate-300 rounded">Day</button>
              <button className="px-3 py-1 text-sm bg-slate-600 text-slate-300 rounded">Week</button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-8 gap-1 text-xs text-slate-400 mb-2">
              <div></div>
              <div className="text-center">6AM</div>
              <div className="text-center">9AM</div>
              <div className="text-center">12PM</div>
              <div className="text-center">3PM</div>
              <div className="text-center">6PM</div>
              <div className="text-center">9PM</div>
              <div className="text-center">12AM</div>
            </div>
            
            {heatMapData.map((row) => (
              <div key={row.day} className="grid grid-cols-8 gap-1">
                <div className="text-xs text-slate-400 py-1">{row.day}</div>
                {Object.entries(row).slice(1).map(([time, value]) => (
                  <div
                    key={time}
                    className={cn(
                      'h-6 rounded text-xs flex items-center justify-center text-white',
                      getHeatmapColor(value as number)
                    )}
                  >
                    {value || ''}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center space-x-4 text-xs">
            <span className="text-slate-400">🟦 High (&gt;25%)</span>
            <span className="text-slate-400">🟨 Medium (15-25%)</span>
            <span className="text-slate-400">🟫 Low (&lt;15%)</span>
          </div>

          <div className="mt-2 text-sm text-slate-400">
            Peak Times: Tue-Thu 9AM-6PM | Avoid: Fri PM, Weekends
          </div>
        </div>
      </div>

      {/* Cohort Analysis */}
      <div className="bg-slate-700 rounded-lg border border-slate-600 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-50">
            Lead Cohort Analysis
          </h2>
          <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-500">
            Export CSV
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-600">
                <th className="text-left text-sm font-medium text-slate-300 pb-3">Cohort</th>
                <th className="text-left text-sm font-medium text-slate-300 pb-3">Size</th>
                <th className="text-left text-sm font-medium text-slate-300 pb-3">Week 1</th>
                <th className="text-left text-sm font-medium text-slate-300 pb-3">Week 2</th>
                <th className="text-left text-sm font-medium text-slate-300 pb-3">Week 4</th>
                <th className="text-left text-sm font-medium text-slate-300 pb-3">Week 8</th>
                <th className="text-left text-sm font-medium text-slate-300 pb-3">Final</th>
              </tr>
            </thead>
            <tbody>
              {cohortData.map((cohort, index) => (
                <tr 
                  key={cohort.cohort}
                  className={cn(
                    'border-b border-slate-600/50',
                    index % 2 === 0 ? 'bg-slate-700/50' : 'bg-slate-800/30'
                  )}
                >
                  <td className="py-2 pr-4 text-sm text-slate-300">{cohort.cohort}</td>
                  <td className="py-2 pr-4 text-sm text-slate-300">{cohort.size}</td>
                  <td className="py-2 pr-4 text-sm text-slate-300">{formatPercentage(cohort.week1)}</td>
                  <td className="py-2 pr-4 text-sm text-slate-300">{formatPercentage(cohort.week2)}</td>
                  <td className="py-2 pr-4 text-sm text-slate-300">{formatPercentage(cohort.week4)}</td>
                  <td className="py-2 pr-4 text-sm text-slate-300">{formatPercentage(cohort.week8)}</td>
                  <td className="py-2 text-sm text-slate-300">{formatPercentage(cohort.final)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 p-4 bg-slate-800 rounded">
          <h4 className="text-sm font-medium text-slate-300 mb-2">Trend Analysis:</h4>
          <ul className="text-sm text-slate-400 space-y-1">
            <li>• Week 1 retention stable (~72-75%)</li>
            <li>• Significant drop Week 1→2 (opportunity for optimization)</li>
            <li>• Final conversion improving (+2.3% over 6 weeks)</li>
            <li>• March cohorts performing 8% better than February</li>
          </ul>
        </div>
      </div>

      {/* ROI Calculator */}
      <div className="bg-slate-700 rounded-lg border border-slate-600 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-50">
            ROI Analysis
          </h2>
          <button className="px-3 py-1 text-sm bg-slate-600 text-slate-300 rounded hover:bg-slate-500">
            Customize
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-3">Investment (Monthly):</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-400">
                <span>• Technology costs:</span>
                <span>$15,420</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>• Content creation:</span>
                <span>$8,500</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>• Operations:</span>
                <span>$12,300</span>
              </div>
              <div className="flex justify-between text-slate-300 font-medium border-t border-slate-600 pt-2">
                <span>Total:</span>
                <span>$36,220</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-3">Returns (Monthly):</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-400">
                <span>• Leads converted:</span>
                <span>$444,132</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>• Time saved:</span>
                <span>$20,400</span>
              </div>
              <div className="flex justify-between text-slate-300 font-medium border-t border-slate-600 pt-2">
                <span>Total:</span>
                <span>$464,532</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-3">Results:</h3>
            <div className="space-y-2">
              <div className="text-emerald-400 font-bold text-lg">
                📈 ROI: 1,282% (12.8x return)
              </div>
              <div className="text-emerald-400 font-medium">
                💰 Net Profit: $428,312/month
              </div>
              <div className="text-blue-400 font-medium">
                ⏱️ Payback Period: 2.8 days
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};